import { Box } from "@mui/material";
import { motion } from "framer-motion";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { CONTROL_HEIGHT, STAFF_PICKER } from "../../../theme/tokens";
import { SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../UIToolkit/Inputs/SystemValidationTextField";
import { CallInTimeField } from "./CallInTimeField";
import type { RemoveStaffDetails } from "./useRemoveStaffDetails";

/** The reason picker is the footer's first decision, so it gets the same width as a step-1 field in Add Staff. */
const REASON_SELECT_SX = { minWidth: STAFF_PICKER.SCOPE_FIELD_MIN_WIDTH, maxWidth: STAFF_PICKER.SCOPE_FIELD_MAX_WIDTH } as const;

/**
 * The slot is exactly one field tall, so the "Pick a reason" error hangs into the footer's bottom padding
 * instead of growing the row — otherwise the popup would grow and the button would slide out from under the
 * pointer on the very click that raised the error.
 */
const REASON_SLOT_SX = { display: "flex", flex: "none", height: CONTROL_HEIGHT.STANDARD } as const;

/** The reason (required in every department, nothing pre-selected) — it sits in the footer's action row. */
export const RemoveStaffReasonField: FC<{ details: RemoveStaffDetails }> = ({ details }) => {
	const { t } = useTranslation();
	const { reasonOptions } = details;
	return (
		<Box sx={REASON_SLOT_SX}>
			<motion.div animate={details.shakeControls} style={{ display: "flex", flexDirection: "column" }}>
				<SystemDropdown
					selectedItem={details.selectedReason}
					items={reasonOptions.options}
					selectSx={REASON_SELECT_SX}
					onChange={details.changeReason}
					label={t("removal.reason.label")}
					placeholder={t("remove.staff.reason.placeholder")}
					required
					errorKey={details.showReasonError ? "remove.staff.reason.required" : undefined}
					loading={reasonOptions.isLoading}
					disabled={reasonOptions.isLoading}
					data-testid="nurse-removal-reason-dropdown"
				/>
			</motion.div>
		</Box>
	);
};

/**
 * What a picked reason adds: an optional note saved with the removal, and when they called in for a call-in
 * reason. Nothing renders before a reason is picked, so the footer stays one row until then.
 */
export const RemoveStaffExtraFields: FC<{ details: RemoveStaffDetails }> = ({ details }) => {
	const { t } = useTranslation();
	if (!details.selectedReason) return null;
	return (
		<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }} data-testid="remove-staff-details">
			<SystemValidationTextField
				reserveHeightForHelperText={false}
				label={t("remove.staff.message.label")}
				value={details.removalMessage}
				inputSx={REASON_SELECT_SX}
				onChange={(event) => details.setRemovalMessage(event.target.value)}
				onClear={() => details.setRemovalMessage("")}
				dataTestId="remove-staff-message-input"
			/>
			{details.isCallInReason && <CallInTimeField value={details.callInTime} onChange={details.setCallInTime} />}
		</Box>
	);
};
